import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import { BugTracker } from 'kb-shared/utilities/bugTracker';

import { EXPORT_POLL_INTERVAL_MS } from './MedicalRecords.constants';
import {
  GENERATE_MEDICAL_RECORDS_EXPORT,
  GenerateMedicalRecordsExportResponse,
  GenerateMedicalRecordsExportVariables,
  MEDICAL_RECORDS_EXPORT,
  MedicalRecordsExportResponse,
  MedicalRecordsExportVariables
} from './MedicalRecords.graphql';
import { DateRange } from './MedicalRecords.types';

export const useMedicalRecordsExport = () => {
  const [exportId, setExportId] = useState<string>();
  const [requesting, setRequesting] = useState(false);
  const [failed, setFailed] = useState(false);
  // Incremented on every generate/reset, so the response of a superseded request is ignored
  const attempt = useRef(0);

  const [generateExport] = useMutation<
    GenerateMedicalRecordsExportResponse,
    GenerateMedicalRecordsExportVariables
  >(GENERATE_MEDICAL_RECORDS_EXPORT);

  const { data, stopPolling } = useQuery<
    MedicalRecordsExportResponse,
    MedicalRecordsExportVariables
  >(MEDICAL_RECORDS_EXPORT, {
    variables: { id: exportId || '' },
    skip: !exportId,
    fetchPolicy: 'network-only',
    pollInterval: EXPORT_POLL_INTERVAL_MS,
    onError: error => {
      BugTracker.notify(error, 'Failed to fetch medical records export');
      setExportId(undefined);
      setFailed(true);
    }
  });

  const medicalRecordsExport = exportId ? data?.medicalRecordsExport : undefined;
  const status = medicalRecordsExport?.status;
  // The export isn't found, e.g. it doesn't belong to the logged in patient
  const exportNotFound =
    Boolean(exportId) && data !== undefined && data.medicalRecordsExport === null;

  useEffect(() => {
    if (exportNotFound) {
      stopPolling();
      BugTracker.notify(
        `Medical records export ${exportId} not found`,
        'Medical records export not found'
      );
      return;
    }
    if (status && status !== 'PENDING') stopPolling();
  }, [status, exportNotFound, exportId, stopPolling]);

  const generate = (dateRange: DateRange) => {
    const currentAttempt = ++attempt.current;
    setExportId(undefined);
    setFailed(false);
    setRequesting(true);

    generateExport({
      variables: { dateRange },
      onCompleted: response => {
        if (currentAttempt !== attempt.current) return;

        setRequesting(false);
        setExportId(response.generateMedicalRecordsExport.medicalRecordsExport.id);
      },
      onError: error => {
        if (currentAttempt !== attempt.current) return;

        BugTracker.notify(error, 'Failed to request medical records export');
        setRequesting(false);
        setFailed(true);
      }
    });
  };

  const reset = () => {
    attempt.current += 1;
    setExportId(undefined);
    setRequesting(false);
    setFailed(false);
  };

  const exportFailed = failed || exportNotFound || status === 'FAILED';
  const generating = !exportFailed && (requesting || (Boolean(exportId) && status !== 'COMPLETED'));
  const downloadUrl = status === 'COMPLETED' ? medicalRecordsExport?.downloadUrl : null;

  return { generate, reset, generating, failed: exportFailed, downloadUrl };
};
